import { feature, featureCollection } from "@turf/turf";
import { FeatureCollection } from "geojson";
import { useMemo } from "react";

import { GqlPlannerTravelGroupOwnerBillingType, GqlPlannerV2MapElementPointType } from "~/__generated__";
import { FIELDWORKER_COLORS } from "~/config/statusColors";
import GaiaMapDataLayer from "~/modules/map/components/GaiaMapDataLayer";
import { GaiaMapStyleCircle } from "~/modules/map/components/GaiaMapStyleComposites/GaiaMapStyleCircle";
import { LAYER_DEFAULTS } from "~/modules/map/components/GaiaMapStyleComposites/layerDefaults";
import {
    IMG_MARKER_CIRCLE_DASHED_STROKE,
    IMG_MARKER_FIELDWORKER_CAR,
    IMG_MARKER_FIELDWORKER_MYWHEELS,
    IMG_MARKER_FIELDWORKER_PICK_UP,
} from "~/modules/map/mapStyles";

import { PickupPointLayerFragment } from "./fragments.generated";

interface PickupPointLayerProps {
    layerId: string;
    points: PickupPointLayerFragment[];
    ownerTravelMethod: GqlPlannerTravelGroupOwnerBillingType;

    excludeId?: string;
}

export interface PickupPointProperties {
    _id: string;
    id: string;
    numberOfPlannerShiftsEnteringExiting: number;
    type: GqlPlannerV2MapElementPointType;
    icon: "pickup" | "myWheels" | "privateCar";
    isDraggable: boolean;
    names: string;
    shiftIds: string[];
}

function usePickupPointsGeoJson(
    points: PickupPointLayerFragment[],
    ownerTravelMethod: GqlPlannerTravelGroupOwnerBillingType,
): FeatureCollection {
    return useMemo(() => {
        const features = points.map(point => {
            const properties: PickupPointProperties = {
                _id: point._id,
                id: point._id, // Also set id for drag listener compatibility
                numberOfPlannerShiftsEnteringExiting: point.numberOfPlannerShiftsEnteringExiting,
                type: point.type,
                // Display properties
                icon:
                    point.type === GqlPlannerV2MapElementPointType.OwnerStartExit && ownerTravelMethod !== "none"
                        ? ownerTravelMethod
                        : "pickup",
                isDraggable: point.type !== GqlPlannerV2MapElementPointType.OwnerStartExit,
                names:
                    point.plannerShiftTravels
                        ?.map(travel => travel.plannerShift?.fieldworker.user.firstName)
                        .filter(Boolean)
                        .join(", ") ?? "",
                shiftIds: point.plannerShiftTravels?.map(t => t.plannerShiftId) ?? [],
            };
            return feature(point.locationPoint, properties);
        });
        return featureCollection(features);
    }, [points, ownerTravelMethod]);
}

export function PickupPointLayer({ points, excludeId, layerId, ownerTravelMethod }: PickupPointLayerProps) {
    const data = usePickupPointsGeoJson(points, ownerTravelMethod);

    return (
        <>
            <GaiaMapDataLayer id={`${layerId}_data`} data={data} />

            <GaiaMapStyleCircle
                id={layerId}
                source={`${layerId}_data`}
                color={FIELDWORKER_COLORS.available}
                icon={{
                    color: LAYER_DEFAULTS.iconColor.onMarker,
                    image: [
                        "case",
                        ["==", ["get", "icon"], "pickup"],
                        IMG_MARKER_FIELDWORKER_PICK_UP,
                        ["==", ["get", "icon"], "myWheels"],
                        IMG_MARKER_FIELDWORKER_MYWHEELS,
                        ["==", ["get", "icon"], "privateCar"],
                        IMG_MARKER_FIELDWORKER_CAR,
                        "",
                    ],
                }}
                label={{
                    textField: ["get", "names"],
                    color: "available",
                    filter: ["all", ["!=", ["get", "_id"], excludeId ?? ""], ["!=", ["get", "names"], ""]],
                }}
                badge={{
                    countField: ["get", "numberOfPlannerShiftsEnteringExiting"],
                    minCount: 2,
                    filter: [
                        "all",
                        ["!=", ["get", "_id"], excludeId ?? ""],
                        [">", ["get", "numberOfPlannerShiftsEnteringExiting"], 1],
                    ],
                }}
                draggable={{
                    image: IMG_MARKER_CIRCLE_DASHED_STROKE,
                    opacity: 0.4,
                    filter: ["all", ["!=", ["get", "_id"], excludeId ?? ""], ["==", ["get", "isDraggable"], true]],
                }}
                filter={["!=", ["get", "_id"], excludeId ?? ""]}
            />
        </>
    );
}
